/** @module Background runs: what is going on now, what may be started, and what happened before. */
import { useTranslation } from "react-i18next";

import { count, duration } from "@/lib/format";
import { JobStarters } from "@/features/admin/job-starters";
import { type Job, useCancelJob, useJobs, useSchedules } from "@/features/admin/use-admin";

const TONE: Record<Job["status"], string> = {
  queued: "var(--text-muted)",
  running: "var(--accent)",
  succeeded: "var(--gain)",
  failed: "var(--loss)",
  cancelled: "var(--warn)",
};

const OPEN = new Set<Job["status"]>(["queued", "running"]);

function share(job: Job): number | null {
  if (!job.progress_total) return null;
  return Math.min(1, job.progress_current / job.progress_total);
}

/** Seconds a job took, or has been taking so far. */
function ran(job: Job): number | null {
  if (!job.started_at) return null;
  const end = job.finished_at ? new Date(job.finished_at) : new Date();
  return (end.getTime() - new Date(job.started_at).getTime()) / 1000;
}

/** The first line of a database error says what went wrong; the rest is the statement. */
function splitError(error: string): { head: string; rest: string } {
  const cut = error.search(/\s\[SQL:|\n/);
  if (cut < 0) return { head: error, rest: "" };
  return { head: error.slice(0, cut).trim(), rest: error.slice(cut).trim() };
}

/** What a finished run produced, as "key: value" pairs the API sent back. */
function Outcome({ result }: { result: Record<string, unknown> }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const pairs = Object.entries(result).filter(
    ([, value]) => typeof value === "number" || typeof value === "string",
  );
  if (pairs.length === 0) return null;

  return (
    <dl className="flex flex-wrap gap-x-4 gap-y-1 t-xs t-muted">
      {pairs.map(([key, value]) => (
        <div key={key} className="flex gap-1">
          <dt>{t(`admin.jobs.result.${key}`, key)}:</dt>
          <dd className="num text-(--text)">
            {typeof value === "number" ? count(value, locale) : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function JobRow({ job }: { job: Job }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const cancel = useCancelJob();
  const done = share(job);
  const seconds = ran(job);
  const error = job.error ? splitError(job.error) : null;
  const mode = typeof job.params.mode === "string" ? job.params.mode : null;

  return (
    <li className="flex flex-col gap-2 border-t border-(--border) py-3 first:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">
          {t(`admin.jobs.types.${job.type}`, job.type)}
          {mode ? (
            <span className="ml-2 t-xs t-muted">{t(`admin.jobs.modes.${mode}`, mode)}</span>
          ) : null}
        </span>
        <span className="flex flex-wrap items-baseline gap-3 text-[length:var(--text-sm)]">
          <span style={{ color: TONE[job.status] }}>{t(`admin.jobs.status.${job.status}`)}</span>
          {seconds !== null ? (
            <span className="num text-(--text-muted)">{duration(seconds)}</span>
          ) : null}
          <span className="t-muted">
            {t("admin.jobs.startedBy", {
              who: job.triggered_by_name ?? t("admin.jobs.automatic"),
            })}
          </span>
          <span className="t-muted">
            {new Date(job.created_at).toLocaleString(locale, {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </span>
        </span>
      </div>

      {done !== null ? (
        <div className="flex items-center gap-3">
          <div className="h-2 grow overflow-hidden rounded-[var(--radius-pill)] bg-(--border)">
            <div
              className="h-full rounded-[var(--radius-pill)]"
              style={{ width: `${done * 100}%`, background: TONE[job.status] }}
            />
          </div>
          <span className="num shrink-0 t-sm t-muted">
            {count(job.progress_current, locale)} / {count(job.progress_total ?? 0, locale)}
          </span>
        </div>
      ) : null}

      {job.message ? <p className="t-sm t-muted">{job.message}</p> : null}
      <Outcome result={job.result} />

      {error ? (
        <div className="text-[length:var(--text-sm)]">
          <p className="break-words text-(--loss)">{error.head}</p>
          {error.rest ? (
            // The full statement is thousands of characters; it must not own the page.
            <details className="mt-1">
              <summary className="cursor-pointer t-xs t-muted">
                {t("admin.jobs.showDetail")}
              </summary>
              <pre className="mt-1 max-h-48 overflow-auto rounded-[var(--radius-sm)] bg-(--surface) p-2 text-[length:var(--text-xs)] whitespace-pre-wrap">
                {error.rest}
              </pre>
            </details>
          ) : null}
        </div>
      ) : null}

      {OPEN.has(job.status) ? (
        <div>
          <button
            type="button"
            disabled={cancel.isPending || job.cancel_requested}
            onClick={() => {
              cancel.mutate(job.id);
            }}
            className="min-h-9 rounded-[var(--radius-pill)] border border-(--border) px-4 text-[length:var(--text-sm)] disabled:opacity-50"
          >
            {job.cancel_requested ? t("admin.jobs.stopping") : t("admin.jobs.stop")}
          </button>
        </div>
      ) : null}
    </li>
  );
}

function Schedules() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const schedules = useSchedules();
  const rows = schedules.data ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="flex flex-col gap-2 panel panel-lg">
      <h3 className="t-h3">{t("admin.schedules.title")}</h3>
      <table className="w-full text-[length:var(--text-sm)]">
        <thead>
          <tr className="text-left text-(--text-muted)">
            <th className="py-1">{t("admin.schedules.what")}</th>
            <th className="py-1">{t("admin.schedules.next")}</th>
            <th className="py-1 text-right">{t("admin.schedules.last")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-(--border)">
              <td className="py-1">
                {t(`admin.schedules.keys.${row.key}`, row.key)}
                {row.on_host ? (
                  <span className="ml-2 t-xs t-muted">{t("admin.schedules.onHost")}</span>
                ) : null}
                <span className="ml-2 font-mono text-[length:var(--text-xs)] text-(--text-faint)">
                  {row.cron}
                </span>
              </td>
              <td className="py-1">
                {row.next_run ? new Date(row.next_run).toLocaleString(locale) : "–"}
              </td>
              <td className="py-1 text-right">
                {row.last_run ? (
                  <span style={{ color: TONE[row.last_run.status] }}>
                    {t(`admin.jobs.status.${row.last_run.status}`)}
                  </span>
                ) : (
                  <span className="t-muted">{t("admin.schedules.never")}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function JobsPanel() {
  const { t } = useTranslation();
  const jobs = useJobs();
  const all = jobs.data ?? [];
  const running = all.filter((job) => OPEN.has(job.status));
  const history = all.filter((job) => !OPEN.has(job.status));

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-2 panel panel-lg">
        <h3 className="t-h3">{t("admin.jobs.now")}</h3>
        {jobs.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
        {jobs.isSuccess && running.length === 0 ? (
          <p className="t-muted">{t("admin.jobs.idle")}</p>
        ) : null}
        <ul>
          {running.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </ul>
      </section>

      <JobStarters running={running} />

      <Schedules />

      <section className="panel panel-lg">
        <h3 className="mb-2 t-h3">{t("admin.jobs.history")}</h3>
        {jobs.isSuccess && history.length === 0 ? (
          <p className="t-muted">{t("admin.jobs.empty")}</p>
        ) : null}
        <ul>
          {history.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </ul>
      </section>
    </div>
  );
}
